import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Extension } from '@tiptap/react'

import {
  classifyPlaceholder,
  LEAF_TEXT,
  PLACEHOLDER_STATUS,
  scanPlaceholderSegments
} from '../../../helpers/mail-template-placeholders'

// Hebt {{…}} im Editor als Chip hervor, ungültige rot. Reine Darstellung über
// Decorations, im Dokument bleibt der Platzhalter normaler Text. So bleibt das
// Speicherformat gleich und inviteTalent kann die Vorlage weiter mit
// generateHTML(json, [StarterKit]) rendern.
//
// Die Erkennung ist dieselbe wie beim Prüfen vor dem Speichern
// (helpers/mail-template-placeholders).

const placeholderTokensKey = new PluginKey('placeholderTokens')

const marksKey = (marks) =>
  marks
    .map(({ type, attrs }) => `${type.name}${JSON.stringify(attrs)}`)
    .sort()
    .join('|')

const buildDecorations = (doc, { known, allowed, describe }) => {
  const decorations = []
  let invalidCount = 0

  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true

    const segments = []
    node.forEach((child) => {
      segments.push(
        child.isText
          ? { text: child.text, marksKey: marksKey(child.marks) }
          : { text: LEAF_TEXT, marksKey: '', leaf: true }
      )
    })

    scanPlaceholderSegments(segments).forEach((match) => {
      const status = classifyPlaceholder(match, { known, allowed })
      const valid = status === PLACEHOLDER_STATUS.VALID
      if (!valid) invalidCount += 1

      const title = describe(match.token, status)
      decorations.push(
        Decoration.inline(pos + 1 + match.start, pos + 1 + match.end, {
          class: valid
            ? 'mail-placeholder'
            : 'mail-placeholder mail-placeholder--invalid',
          'data-placeholder-status': status,
          ...(title ? { title } : {})
        })
      )
    })

    return false
  })

  return { decorations: DecorationSet.create(doc, decorations), invalidCount }
}

/**
 * Ändert erlaubte Platzhalter oder Beschreibungen, ohne den Editor neu zu
 * erzeugen. Die Transaktion ändert das Dokument nicht, onUpdate feuert also nicht.
 */
export const setPlaceholderConfig = (editor, config) => {
  if (!editor || editor.isDestroyed) return
  editor.view.dispatch(editor.state.tr.setMeta(placeholderTokensKey, config))
}

/**
 * Backspace direkt hinter bzw. Entf direkt vor einem bekannten Platzhalter
 * löscht ihn ganz. Rückgängig machen stellt ihn wieder her.
 *
 * @param {'backward'|'forward'} direction
 * @param {string[]} known
 */
export const deletePlaceholder =
  (direction, known) => (state, dispatch, view) => {
    const { selection } = state
    if (!selection.empty || view?.composing) return false

    const { $from } = selection
    const { parent, parentOffset } = $from
    const longest = Math.max(...known.map((token) => token.length))
    const backward = direction === 'backward'

    const text = backward
      ? parent.textBetween(
          Math.max(0, parentOffset - longest),
          parentOffset,
          undefined,
          LEAF_TEXT
        )
      : parent.textBetween(
          parentOffset,
          Math.min(parent.content.size, parentOffset + longest),
          undefined,
          LEAF_TEXT
        )

    const token = known.find((candidate) =>
      backward ? text.endsWith(candidate) : text.startsWith(candidate)
    )
    if (!token) return false

    const from = backward ? $from.pos - token.length : $from.pos
    if (dispatch) {
      dispatch(state.tr.delete(from, from + token.length).scrollIntoView())
    }
    return true
  }

/**
 * Fügt einen Platzhalter ohne Formatierung ein. Fett oder kursiv mitten im
 * Token würde ihn für inviteTalent unbrauchbar machen. Weitergetippt wird in
 * der Formatierung der Umgebung.
 *
 * @param {object} editor
 * @param {string} token
 * @param {{ range?: { from: number, to: number } }} [options] Einfügeposition, sonst die Auswahl
 */
export const insertPlaceholder = (editor, token, { range } = {}) =>
  editor
    .chain()
    .focus()
    .command(({ tr, state }) => {
      const size = tr.doc.content.size
      const from = Math.min(range?.from ?? state.selection.from, size)
      const to = Math.max(from, Math.min(range?.to ?? state.selection.to, size))
      const marks = state.storedMarks || state.doc.resolve(from).marks()

      tr.replaceWith(from, to, state.schema.text(token))
      tr.setSelection(TextSelection.create(tr.doc, from + token.length))
      tr.setStoredMarks(marks)
      tr.scrollIntoView()
      return true
    })
    .run()

export const PlaceholderTokens = Extension.create({
  name: 'placeholderTokens',

  // Vor der Backspace-Kette von core und StarterKit
  priority: 1000,

  addOptions() {
    return { known: [], allowed: [], describe: () => null }
  },

  addKeyboardShortcuts() {
    const { known } = this.options

    return {
      Backspace: ({ editor }) =>
        deletePlaceholder('backward', known)(
          editor.state,
          editor.view.dispatch,
          editor.view
        ),
      Delete: ({ editor }) =>
        deletePlaceholder('forward', known)(
          editor.state,
          editor.view.dispatch,
          editor.view
        )
    }
  },

  addProseMirrorPlugins() {
    const { known, allowed, describe } = this.options

    return [
      new Plugin({
        key: placeholderTokensKey,
        state: {
          init: (_, state) => {
            const config = { known, allowed, describe }
            return { config, ...buildDecorations(state.doc, config) }
          },
          apply: (tr, previous, _oldState, newState) => {
            const meta = tr.getMeta(placeholderTokensKey)
            if (!meta && !tr.docChanged) return previous

            const config = { ...previous.config, ...meta }
            return { config, ...buildDecorations(newState.doc, config) }
          }
        },
        props: {
          decorations: (state) =>
            placeholderTokensKey.getState(state).decorations,
          attributes: (state) =>
            placeholderTokensKey.getState(state).invalidCount
              ? { 'aria-invalid': 'true' }
              : {}
        }
      })
    ]
  }
})
